import { v4 as uuidv4 } from 'uuid';
import type {
  NavbarData,
  HeroData,
  SplitHeroData,
  FeaturesData,
  FormData,
  TeamComponent,
} from './ComponentEditModal.types';
import type { FormSplitData, NavigationMenuData } from '../../schemas/validation';

/**
 * Format component type names for display
 * @param type - The component type string
 * @returns Formatted display name
 */
export const formatComponentTypeName = (type: string): string => {
  // Special cases for acronyms
  if (type === 'cta') return 'Call To Action';
  if (type === 'form-split') return 'Split Form';
  if (type === 'html-split') return 'Split HTML';
  if (type === 'navigation-menu') return 'Expandable Navigation Bar';

  // Default: capitalize first letter
  return type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Initialize navbar data with proper defaults
 */
export const initializeNavbarData = (data: NavbarData): NavbarData => {
  const result = { ...data };

  // Ensure links have the required order field
  if (result.links) {
    result.links = result.links.map((link: any, index: number) => ({
      ...link,
      order: link.order ?? index,
    }));
  }

  // Ensure siteName exists if no logo is configured
  if (!result.logo && !result.siteName) {
    result.siteName = 'Your Site Name';
  }

  return result;
};

export const initializeNavigationMenuData = (data: NavigationMenuData): NavigationMenuData => {
  const result = { ...data };

  if (result.links) {
    result.links = result.links.map((link, index) => ({
      ...link,
      order: link.order ?? index,
      children: link.children?.map((child, childIndex) => ({
        ...child,
        order: child.order ?? childIndex,
      })),
    }));
  }

  if (!result.logo && !result.siteName) {
    result.siteName = 'Your Site Name';
  }

  return result;
};

/**
 * Initialize hero data with proper defaults
 */
export const initializeHeroData = (data: HeroData): HeroData => {
  const result = { ...data };

  // Ensure buttons have the required order field
  if (result.buttons) {
    result.buttons = result.buttons.map((button: any, index: number) => ({
      ...button,
      order: button.order ?? index,
    }));
  }

  return result;
};

/**
 * Initialize split-hero data with proper defaults
 */
export const initializeSplitHeroData = (data: SplitHeroData): SplitHeroData => {
  const result = { ...data };

  // Ensure buttons have the required order field
  if (result.buttons) {
    result.buttons = result.buttons.map((button: any, index: number) => ({
      ...button,
      order: button.order ?? index,
    }));
  }

  // Ensure imageWidth + textWidth = 100
  if (result.imageWidth !== undefined && result.textWidth !== undefined) {
    const total = result.imageWidth + result.textWidth;
    if (total !== 100) {
      result.textWidth = 100 - result.imageWidth;
    }
  } else if (result.imageWidth === undefined || result.textWidth === undefined) {
    result.imageWidth = result.imageWidth ?? 50;
    result.textWidth = result.textWidth ?? 50;
  }

  if (result.imagePresentation !== 'cutout') {
    result.imagePresentation = 'fill';
  }

  return result;
};

export const initializeHtmlSplitData = (data: unknown): any => {
  const src = (data || {}) as Record<string, unknown>;
  const result: Record<string, unknown> = { ...src };

  if (Array.isArray(result.buttons)) {
    result.buttons = (result.buttons as any[]).map((button: any, index: number) => ({
      ...button,
      order: button.order ?? index,
    }));
  }

  const textWidth = typeof result.textWidth === 'number' ? result.textWidth : 50;
  const htmlWidth = typeof result.htmlWidth === 'number' ? result.htmlWidth : 50;
  if (textWidth + htmlWidth !== 100) {
    result.textWidth = textWidth;
    result.htmlWidth = 100 - textWidth;
  } else {
    result.textWidth = textWidth;
    result.htmlWidth = htmlWidth;
  }

  if (typeof result.htmlMinHeight !== 'number') {
    result.htmlMinHeight = 480;
  }

  if (!result.embed || typeof result.embed !== 'object') {
    result.embed = {
      embedType: 'custom',
      custom: { html: '<p>Paste in custom HTML here</p>', css: '', javascript: '' },
    };
  }

  if (result.stackOnMobile === undefined) {
    result.stackOnMobile = true;
  }

  if (result.layout !== 'html-left' && result.layout !== 'html-right') {
    result.layout = 'html-right';
  }

  return result;
};

/**
 * Initialize features data with proper defaults
 */
export const initializeFeaturesData = (data: FeaturesData): FeaturesData => {
  const result = { ...data };

  // Ensure features have the required order field
  if (result.features) {
    result.features = result.features.map((feature: any, index: number) => ({
      ...feature,
      order: feature.order ?? index,
    }));
  }

  return result;
};

/**
 * Initialize form data with proper defaults
 */
export const initializeFormData = (data: FormData): FormData => {
  const result = { ...data };

  // Ensure fields have required IDs and order
  if (result.fields) {
    result.fields = result.fields.map((field: any, index: number) => ({
      ...field,
      id: field.id || uuidv4(),
      order: field.order ?? index,
    }));
  }

  // Ensure styling has alignment for backwards compatibility
  if (result.styling) {
    result.styling = {
      ...result.styling,
      alignment: result.styling.alignment || 'center',
    };
  }

  return result;
};

const FORM_SPLIT_MEDIA_PLACEHOLDER = 'https://placehold.co/800x600/e2e8f0/64748b/png?text=Media';

/** Conversion popup editor: sync dismiss checkbox with stored flag or legacy label-only payloads */
export const initializeConversionPopupData = (data: unknown): any => {
  const src = data as Record<string, unknown>;
  const label = String(src?.secondaryCtaLabel ?? '').trim();
  const hasStored = typeof src?.dismissButtonEnabled === 'boolean';
  const dismissButtonEnabled = hasStored ? !!src.dismissButtonEnabled : label.length > 0;
  return { ...(data as object), dismissButtonEnabled };
};

/**
 * Initialize split form data (form fields + media defaults)
 */
export const initializeFormSplitData = (data: unknown): FormSplitData => {
  const base = initializeFormData(data as FormData);
  const src = data as Partial<FormSplitData>;
  const hasMediaUrl = src.media && typeof src.media.url === 'string' && src.media.url.length > 0;
  const rawMedia = hasMediaUrl
    ? { ...src.media! }
    : {
        type: 'image' as const,
        url: FORM_SPLIT_MEDIA_PLACEHOLDER,
        alt: '',
      };
  const media =
    rawMedia.type === 'video' && rawMedia.borderRadius === 'circle'
      ? { ...rawMedia, borderRadius: 'large' as const }
      : rawMedia;
  return {
    ...base,
    media,
    mediaPosition: src.mediaPosition ?? 'left',
    stackOnMobile: src.stackOnMobile !== false,
  };
};

/**
 * Initialize team data with proper defaults
 */
export const initializeTeamData = (data: TeamComponent['data']): TeamComponent['data'] => {
  const result = { ...data };

  // Ensure members have required IDs and order
  if (result.members) {
    result.members = result.members.map((member: any, index: number) => ({
      ...member,
      id: member.id || uuidv4(),
      order: member.order ?? index,
    }));
  }

  return result;
};

/**
 * Initialize text-blocks data with proper defaults
 */
export const initializeTextBlocksData = (data: any): any => {
  const result = { ...data };

  // Ensure blocks have required IDs and order
  if (result.blocks) {
    result.blocks = result.blocks.map((block: any, index: number) => ({
      ...block,
      id: block.id || uuidv4(),
      order: block.order ?? index,
    }));
  }

  return result;
};

/**
 * Initialize component data based on type
 */
export const initializeComponentData = (type: string, data: any): any => {
  switch (type) {
    case 'navbar':
      return initializeNavbarData(data);
    case 'navigation-menu':
      return initializeNavigationMenuData(data);
    case 'hero':
      return initializeHeroData(data);
    case 'split-hero':
      return initializeSplitHeroData(data);
    case 'html-split':
      return initializeHtmlSplitData(data);
    case 'features':
      return initializeFeaturesData(data);
    case 'form':
      return initializeFormData(data);
    case 'form-split':
      return initializeFormSplitData(data);
    case 'conversion-popup':
      return initializeConversionPopupData(data);
    case 'team':
      return initializeTeamData(data);
    case 'text-blocks':
      return initializeTextBlocksData(data);
    default:
      return { ...data };
  }
};

/**
 * Clear custom colors for a component based on its type
 * Returns updated form data with null values for color properties
 */
export const clearCustomColors = (componentType: string, formData: any): any => {
  const updatedFormData = { ...formData };

  switch (componentType) {
    case 'hero':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.customColors = null;
      break;

    case 'split-hero':
    case 'html-split':
    case 'simple-header':
    case 'cta':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.customColors = null;
      break;

    case 'features':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.titleColor = null;
      updatedFormData.customColors = null;
      break;

    case 'pricing':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.cardBackgroundColor = null;
      updatedFormData.cardTextColor = null;
      updatedFormData.accentColor = null;
      updatedFormData.highlightBackgroundColor = null;
      updatedFormData.highlightTextColor = null;
      updatedFormData.titleColor = null;
      updatedFormData.subtitleColor = null;
      updatedFormData.descriptionColor = null;
      updatedFormData.customColors = null;
      break;

    case 'team':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.cardBackgroundColor = null;
      updatedFormData.cardTextColor = null;
      updatedFormData.accentColor = null;
      updatedFormData.customColors = null;
      break;

    case 'media-text':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.titleColor = null;
      updatedFormData.subtitleColor = null;
      updatedFormData.customColors = null;
      break;

    case 'text-blocks':
      updatedFormData.backgroundColor = null;
      updatedFormData.defaultTitleColor = null;
      updatedFormData.defaultContentColor = null;
      updatedFormData.customColors = null;
      break;

    case 'title-subtitle':
      updatedFormData.backgroundColor = null;
      updatedFormData.titleColor = null;
      updatedFormData.subtitleColor = null;
      updatedFormData.customColors = null;
      break;

    case 'contact-info':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.accentColor = null;
      updatedFormData.customColors = null;
      break;

    case 'form':
      if (updatedFormData.styling) {
        updatedFormData.styling.backgroundColor = null;
        updatedFormData.styling.textColor = null;
        updatedFormData.styling.borderColor = null;
      }
      updatedFormData.customColors = null;
      break;

    case 'form-split':
      if (updatedFormData.styling) {
        updatedFormData.styling.backgroundColor = null;
        updatedFormData.styling.textColor = null;
        updatedFormData.styling.borderColor = null;
      }
      updatedFormData.customColors = null;
      break;

    case 'video':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.titleColor = null;
      updatedFormData.subtitleColor = null;
      updatedFormData.descriptionColor = null;
      updatedFormData.customColors = null;
      break;

    case 'faq':
    case 'testimonials':
    case 'promo-banner':
    case 'logocloud':
    case 'stats':
    case 'blog-preview':
    case 'conversion-popup':
      updatedFormData.customColors = null;
      break;

    case 'countdown-timer':
      updatedFormData.customColors = null;
      updatedFormData.headlineColor = null;
      updatedFormData.subheadlineColor = null;
      updatedFormData.numberColor = null;
      updatedFormData.labelColor = null;
      updatedFormData.textColor = null;
      updatedFormData.backgroundColor = null;
      break;

    case 'image-gallery':
      updatedFormData.customColors = null;
      updatedFormData.textColor = null;
      updatedFormData.titleColor = null;
      updatedFormData.subtitleColor = null;
      updatedFormData.captionColor = null;
      updatedFormData.filterButtonColor = null;
      updatedFormData.filterButtonActiveColor = null;
      updatedFormData.backgroundColor = null;
      break;

    case 'image-info':
      updatedFormData.customColors = null;
      updatedFormData.textColor = null;
      updatedFormData.titleColor = null;
      updatedFormData.subtitleColor = null;
      updatedFormData.imageBorderColor = null;
      updatedFormData.backgroundColor = null;
      break;

    case 'image-card':
      updatedFormData.customColors = null;
      break;

    case 'navbar':
    case 'navigation-menu':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.customColors = null;
      if (updatedFormData.logo) {
        updatedFormData.logo.textColor = null;
      }
      break;

    case 'footer':
      updatedFormData.backgroundColor = null;
      updatedFormData.textColor = null;
      updatedFormData.customColors = null;
      break;

    case 'head-content':
    case 'body-end':
    case 'html-embed':
      updatedFormData.customColors = null;
      break;

    default:
      console.warn('Use Theme Colors not implemented for component type:', componentType);
  }

  return updatedFormData;
};
